/**
 * Theme Management Dashboard (Admin)
 * CRUD operations, analytics, and oversight for storefront themes
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import './ThemeManagement.css';

// Industry options
const INDUSTRIES = [
  { value: 'GENERAL', label: 'General' },
  { value: 'FASHION', label: 'Fashion & Clothing' },
  { value: 'KIDS', label: 'Kids & Baby' },
  { value: 'AUTOS', label: 'Auto & Parts' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'FMCG', label: 'FMCG & Groceries' },
  { value: 'TECH', label: 'Tech & Gadgets' },
  { value: 'RESTAURANT', label: 'Restaurant & Food' },
  { value: 'LUXURY', label: 'Luxury & Jewelry' },
  { value: 'HANDMADE', label: 'Handmade & Craft' },
  { value: 'SERVICES', label: 'Services & Portfolio' },
];

// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const StarIcon = () => (
  <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const LockIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const PaletteIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.64 1.5-1.5 0-.35-.14-.66-.36-.92-.23-.32-.36-.73-.36-1.08 0-.84.68-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-5.5-4.5-9-10-9z" />
    <circle cx="6.5" cy="11.5" r="1.5" fill="currentColor" />
    <circle cx="10.5" cy="7.5" r="1.5" fill="currentColor" />
    <circle cx="15.5" cy="7.5" r="1.5" fill="currentColor" />
    <circle cx="18.5" cy="11.5" r="1.5" fill="currentColor" />
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 20V10M12 20V4M6 20v-6" />
  </svg>
);

function ThemeManagement() {
  const [themes, setThemes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('themes');
  const [editingTheme, setEditingTheme] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch themes and analytics
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both endpoints, but handle failures individually
      const [themesResult, analyticsResult] = await Promise.allSettled([
        api.get('/themes/admin/all'),
        api.get('/themes/admin/analytics'),
      ]);

      // Handle themes response (api returns data directly, not wrapped in .data)
      if (themesResult.status === 'fulfilled' && themesResult.value?.success) {
        setThemes(themesResult.value.themes);
      } else if (themesResult.status === 'rejected') {
        console.error('Failed to fetch themes:', themesResult.reason);
      }

      // Handle analytics response
      if (analyticsResult.status === 'fulfilled' && analyticsResult.value?.success) {
        setAnalytics(analyticsResult.value.analytics);
      } else if (analyticsResult.status === 'rejected') {
        console.error('Failed to fetch analytics:', analyticsResult.reason);
      }
    } catch (error) {
      console.error('Failed to fetch theme data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };

  // Handle delete (soft delete)
  const handleDelete = async (themeId) => {
    if (!window.confirm('Are you sure you want to deactivate this theme?')) return;

    try {
      await api.delete(`/themes/admin/${themeId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete theme:', error);
    }
  };

  // Handle toggle featured
  const handleToggleFeatured = async (theme) => {
    try {
      await api.put(`/themes/admin/${theme.id}`, {
        isFeatured: !theme.isFeatured,
      });
      fetchData();
    } catch (error) {
      console.error('Failed to toggle featured:', error);
    }
  };

  // Handle toggle active
  const handleToggleActive = async (theme) => {
    try {
      await api.put(`/themes/admin/${theme.id}`, {
        isActive: !theme.isActive,
      });
      fetchData();
    } catch (error) {
      console.error('Failed to toggle active:', error);
    }
  };

  // Handle seed themes
  const [seeding, setSeeding] = useState(false);
  const handleSeedThemes = async () => {
    if (!window.confirm('This will create/update pre-built themes with sample content. Continue?')) return;
    setSeeding(true);
    try {
      const res = await api.post('/themes/admin/seed');
      if (res.success) {
        alert(`Seeded themes! Created: ${res.created?.join(', ') || 'none'}, Updated: ${res.updated?.join(', ') || 'none'}`);
        fetchData();
      }
    } catch (error) {
      console.error('Failed to seed themes:', error);
      alert('Failed to seed themes: ' + (error.message || 'Unknown error'));
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="thm-loading">
        <div className="thm-loading__spinner" />
        <p>Loading theme data...</p>
      </div>
    );
  }

  return (
    <div className="thm-container">
      {/* Header */}
      <div className="thm-header">
        <div className="thm-header__content">
          <div className="thm-header__icon">
            <PaletteIcon />
          </div>
          <div>
            <h1 className="thm-header__title">Theme Management</h1>
            <p className="thm-header__subtitle">Manage storefront themes and customization</p>
          </div>
        </div>
        <div className="thm-header__actions">
          <button
            className="thm-header__seed-btn"
            onClick={handleSeedThemes}
            disabled={seeding}
          >
            {seeding ? 'Seeding...' : 'Seed Pre-built Themes'}
          </button>
          <button className="thm-header__add-btn" onClick={() => setShowCreateModal(true)}>
            <PlusIcon />
            Add Theme
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="thm-stats">
          <div className="thm-stat-card">
            <span className="thm-stat-card__label">Total Themes</span>
            <span className="thm-stat-card__value">{analytics.totalThemes || 0}</span>
          </div>
          <div className="thm-stat-card">
            <span className="thm-stat-card__label">Total Purchases</span>
            <span className="thm-stat-card__value">{analytics.totalPurchases || 0}</span>
          </div>
          <div className="thm-stat-card">
            <span className="thm-stat-card__label">Recent (30d)</span>
            <span className="thm-stat-card__value">{analytics.recentPurchases || 0}</span>
          </div>
          <div className="thm-stat-card thm-stat-card--highlight">
            <span className="thm-stat-card__label">Total Revenue</span>
            <span className="thm-stat-card__value">{formatCurrency(analytics.totalRevenue || 0)}</span>
          </div>
          <div className="thm-stat-card">
            <span className="thm-stat-card__label">Active Stores</span>
            <span className="thm-stat-card__value">{analytics.totalActiveStores || 0}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="thm-tabs">
        <button
          className={`thm-tab ${activeTab === 'themes' ? 'thm-tab--active' : ''}`}
          onClick={() => setActiveTab('themes')}
        >
          <PaletteIcon />
          All Themes
        </button>
        <button
          className={`thm-tab ${activeTab === 'analytics' ? 'thm-tab--active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <ChartIcon />
          Analytics
        </button>
      </div>

      {/* Themes Table */}
      {activeTab === 'themes' && (
        <div className="thm-table-container">
          <table className="thm-table">
            <thead>
              <tr>
                <th>Theme</th>
                <th>Industry</th>
                <th>Price</th>
                <th>Purchases</th>
                <th>Active Stores</th>
                <th>Revenue</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {themes.map((theme) => (
                <tr key={theme.id} className={!theme.isActive ? 'thm-table__row--inactive' : ''}>
                  <td>
                    <div className="thm-theme-name">
                      <span className="thm-theme-name__text">{theme.name}</span>
                      <div className="thm-theme-badges">
                        {theme.isFeatured && (
                          <span className="thm-badge thm-badge--featured" title="Featured">
                            <StarIcon />
                          </span>
                        )}
                        {theme.isPremium && (
                          <span className="thm-badge thm-badge--premium" title="Premium">
                            <LockIcon />
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="thm-industry-tag">{theme.industry?.replace('_', ' ')}</span>
                  </td>
                  <td>
                    {theme.price === 0 ? (
                      <span className="thm-price--free">Free</span>
                    ) : (
                      formatCurrency(theme.price)
                    )}
                  </td>
                  <td>{theme.purchaseCount || 0}</td>
                  <td>{theme.activeStores || 0}</td>
                  <td>{formatCurrency(theme.totalRevenue || 0)}</td>
                  <td>
                    <span className={`thm-status ${theme.isActive ? 'thm-status--active' : 'thm-status--inactive'}`}>
                      {theme.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="thm-actions">
                      <button
                        className="thm-action-btn thm-action-btn--star"
                        onClick={() => handleToggleFeatured(theme)}
                        title={theme.isFeatured ? 'Unfeature' : 'Feature'}
                      >
                        <StarIcon />
                      </button>
                      <button
                        className="thm-action-btn"
                        onClick={() => setEditingTheme(theme)}
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="thm-action-btn thm-action-btn--danger"
                        onClick={() => handleToggleActive(theme)}
                        title={theme.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {theme.isActive ? <TrashIcon /> : <PlusIcon />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {themes.length === 0 && (
            <div className="thm-empty">
              <PaletteIcon />
              <h3>No themes found</h3>
              <p>Create your first theme to get started</p>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="thm-analytics">
          {/* Popular Themes */}
          <div className="thm-analytics-card">
            <h3 className="thm-analytics-card__title">Popular Themes</h3>
            <div className="thm-popular-list">
              {analytics.popularThemes?.map((theme, index) => (
                <div key={theme.id} className="thm-popular-item">
                  <span className="thm-popular-item__rank">{index + 1}</span>
                  <span className="thm-popular-item__name">{theme.name}</span>
                  <span className="thm-popular-item__industry">{theme.industry?.replace('_', ' ')}</span>
                  <span className="thm-popular-item__stores">{theme.activeStores} stores</span>
                </div>
              ))}
              {(!analytics.popularThemes || analytics.popularThemes.length === 0) && (
                <p className="thm-analytics-empty">No data available</p>
              )}
            </div>
          </div>

          {/* Recent Purchases */}
          <div className="thm-analytics-card">
            <h3 className="thm-analytics-card__title">Recent Purchases</h3>
            <div className="thm-purchases-list">
              {analytics.recentPurchasesList?.map((purchase) => (
                <div key={purchase.id} className="thm-purchase-item">
                  <div className="thm-purchase-item__info">
                    <span className="thm-purchase-item__theme">{purchase.theme?.name}</span>
                    <span className="thm-purchase-item__merchant">{purchase.merchant?.email}</span>
                  </div>
                  <span className="thm-purchase-item__price">{formatCurrency(purchase.price)}</span>
                </div>
              ))}
              {(!analytics.recentPurchasesList || analytics.recentPurchasesList.length === 0) && (
                <p className="thm-analytics-empty">No purchases yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTheme) && (
        <ThemeModal
          theme={editingTheme}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTheme(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingTheme(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

// Theme Create/Edit Modal
function ThemeModal({ theme, onClose, onSave }) {
  // Convert kobo to naira for display (divide by 100)
  const priceInNaira = theme?.price ? (theme.price / 100) : 0;

  const [formData, setFormData] = useState({
    name: theme?.name || '',
    slug: theme?.slug || '',
    description: theme?.description || '',
    industry: theme?.industry || 'GENERAL',
    priceNaira: priceInNaira, // Store in Naira for user input
    isPremium: theme?.isPremium || false,
    isFeatured: theme?.isFeatured || false,
    sortOrder: theme?.sortOrder || 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Convert Naira to kobo (multiply by 100) for storage
      const priceInKobo = Math.round(parseFloat(formData.priceNaira) * 100) || 0;

      const payload = {
        ...formData,
        price: priceInKobo, // Send kobo to backend
        sortOrder: parseInt(formData.sortOrder) || 0,
      };
      delete payload.priceNaira; // Remove the display field

      if (theme) {
        await api.put(`/themes/admin/${theme.id}`, payload);
      } else {
        await api.post('/themes/admin/create', payload);
      }

      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="thm-modal-overlay" onClick={onClose}>
      <div className="thm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="thm-modal__header">
          <h2>{theme ? 'Edit Theme' : 'Create Theme'}</h2>
          <button className="thm-modal__close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="thm-modal__body">
            {error && <div className="thm-modal__error">{error}</div>}

            <div className="thm-form-group">
              <label>Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="thm-form-group">
              <label>Slug</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                disabled={!!theme}
              />
            </div>

            <div className="thm-form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="thm-form-row">
              <div className="thm-form-group">
                <label>Industry</label>
                <select name="industry" value={formData.industry} onChange={handleChange}>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind.value} value={ind.value}>
                      {ind.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="thm-form-group">
                <label>Price (₦)</label>
                <input
                  type="number"
                  name="priceNaira"
                  value={formData.priceNaira}
                  onChange={handleChange}
                  min={0}
                  step="0.01"
                  placeholder="e.g. 15000 for ₦15,000"
                />
                <span className="thm-form-hint">Enter amount in Naira (0 for free themes)</span>
              </div>
            </div>

            <div className="thm-form-row">
              <div className="thm-form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  min={0}
                />
              </div>
            </div>

            <div className="thm-form-row thm-form-row--checkboxes">
              <label className="thm-checkbox">
                <input
                  type="checkbox"
                  name="isPremium"
                  checked={formData.isPremium}
                  onChange={handleChange}
                />
                <span>Premium Theme</span>
              </label>

              <label className="thm-checkbox">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                />
                <span>Featured</span>
              </label>
            </div>
          </div>

          <div className="thm-modal__footer">
            <button type="button" className="thm-btn thm-btn--secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="thm-btn thm-btn--primary" disabled={saving}>
              {saving ? 'Saving...' : theme ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ThemeManagement;
